import { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import {
  CodeWrap,
  CodeHeaderBar,
  CodeDots,
  CodeDot,
  CodeHeaderRight,
  LangBadge,
  CodeBody,
  LineNums,
  LineNum,
  CodeTextarea,
  CodeFooter,
  CodeStat,
} from '../styles'
import { detectLang } from '../helpers'

function CodeInput({ value, onChange }) {
  const { t } = useTranslation()
  const taRef = useRef(null)
  const lineNumRef = useRef(null)

  const lines = (value ?? '').split('\n')
  const lang = value?.trim() ? detectLang(value) : 'plain text'

  useEffect(() => {
    if (lineNumRef.current && taRef.current) {
      lineNumRef.current.scrollTop = taRef.current.scrollTop
    }
  })

  function handleKeyDown(e) {
    if (e.key === 'Tab') {
      e.preventDefault()
      const s = e.target.selectionStart
      const end = e.target.selectionEnd
      const next = value.substring(0, s) + '  ' + value.substring(end)
      onChange(next)
      requestAnimationFrame(() => {
        e.target.selectionStart = e.target.selectionEnd = s + 2
      })
    }
  }

  return (
    <CodeWrap>
      {/* Header */}
      <CodeHeaderBar>
        <CodeDots>
          {[0, 1, 2].map((i) => (
            <CodeDot key={i} />
          ))}
        </CodeDots>
        <CodeHeaderRight>
          <LangBadge>{lang}</LangBadge>
        </CodeHeaderRight>
      </CodeHeaderBar>

      {/* Body */}
      <CodeBody>
        <LineNums ref={lineNumRef}>
          {lines.map((_, i) => (
            <LineNum key={i}>{i + 1}</LineNum>
          ))}
        </LineNums>
        <CodeTextarea
          ref={taRef}
          rows={Math.max(8, lines.length)}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onScroll={() => {
            if (lineNumRef.current && taRef.current)
              lineNumRef.current.scrollTop = taRef.current.scrollTop
          }}
          spellCheck={false}
          placeholder={t('assessment.codePlaceholder')}
        />
      </CodeBody>

      {/* Footer */}
      <CodeFooter>
        <CodeStat>
          {lines.length} {lines.length === 1 ? 'line' : 'lines'}
        </CodeStat>
        <CodeStat>
          {(value ?? '').length} {t('assessment.chars')}
        </CodeStat>
      </CodeFooter>
    </CodeWrap>
  )
}

CodeInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
}

export default CodeInput
